import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  PropertyFilter,
  Table,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import {
  defaultWrapLinesPreference,
  i18nStrings,
  mediumPageSizePreference,
  paginationLabels,
} from 'src/commons/tables';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { listDatabases } from 'src/api/catalog';
import { createCatalogDetailLink, createDatabaseDetailLink } from 'src/routes';
import { keyForCatalogNameMap } from 'src/commons/common';
import { EmptyState } from 'src/commons/EmptyState';
import { PageHeader } from 'src/components/common/PageHeader';
import { GetHybridCatalogDatabasesRequest } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';

export interface WSDatabaseListProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  loading: boolean;
  activeWorkspace: any;
  catalogId: any;
  catalogName: string;
  catalogRegion: string;
  clusterIdentifier: string;
  redshiftWorkgroupName: string;
  dataSourceId: string;
  catalogMap: any;
}

export const WSDatabaseList = (props: WSDatabaseListProps) => {
  const [allItems, setItems] = useState([]);
  const [loadingDatabase, setLoadingDatabase] = useState(false);

  const handleRefresh = async () => {
    setLoadingDatabase(true);
    let request: GetHybridCatalogDatabasesRequest = {
      NextToken: null,
      DatabaseKeyList: [
        {
          CatalogId: props.catalogId,
          ClusterIdentifier: props.clusterIdentifier,
          RedshiftWorkgroupName: null,
          Region: props.catalogRegion,
          DataSourceId: props.dataSourceId,
        },
      ],
    };
    let databases = await listDatabases(request);
    let databaseInfoList = databases.DatabaseInfoList;
    while (databases.NextToken != null) {
      request.NextToken = databases.NextToken;
      databases = await listDatabases(request);
      databaseInfoList.push(...databases.DatabaseInfoList);
    }
    var databasesInCatalog = [];
    for (const database of databaseInfoList) {
      if (
        database.CatalogId == props.catalogId &&
        database.Region == props.catalogRegion &&
        database.ClusterIdentifier == props.clusterIdentifier
      ) {
        databasesInCatalog.push(database);
      }
    }
    for (let database of databasesInCatalog) {
      let catalogName = props.catalogMap.get(
        keyForCatalogNameMap(
          database?.DataSourceId,
          database?.CatalogId,
          database?.ClusterIdentifier,
          database?.RedshiftWorkgroupName,
          database?.Region,
        ),
      );
      database.CatalogName = catalogName == null ? database?.CatalogId : catalogName;
    }
    setItems(databasesInCatalog);
    setLoadingDatabase(false);
  };

  useEffect(() => {
    handleRefresh();
  }, [props.catalogId, props.catalogRegion, props.clusterIdentifier, props.redshiftWorkgroupName]);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
  });

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'catalogName',
      header: 'Catalog name',
      cell: (item) => (
        <Link
          href={createCatalogDetailLink(
            item?.DataSourceId,
            item?.CatalogId,
            item?.ClusterIdentifier,
            item?.RedshiftWorkgroupName,
            item?.Region,
          )}
        >
          {item.CatalogName}
        </Link>
      ),
      minWidth: 200,
    },
    {
      id: 'databaseName',
      header: 'Database name',
      cell: (item) => (
        <Link
          href={createDatabaseDetailLink(
            item?.DataSourceId,
            item?.CatalogId,
            item?.ClusterIdentifier,
            item?.RedshiftWorkgroupName,
            item?.DatabaseName,
            item?.Region,
          )}
        >
          {item.DatabaseName}
        </Link>
      ),
      minWidth: 200,
    },
    {
      id: 'description',
      header: 'Database description',
      cell: (item) => item.Description,
      minWidth: 200,
    },
  ];

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: 'Database name',
          key: 'DatabaseName',
          groupValuesLabel: 'Database names',
        },
      ],
    },
  });

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading databases...'
        loading={props.loading || loadingDatabase}
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={<PageHeader buttons={[]} counter={`(${allItems.length})`} header={'Databases'} />}
        empty={
          <EmptyState
            title={'No registered databases'}
            subtitle={'There are no databases registered in this catalog.'}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={mediumPageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={loadingDatabase}
            i18nStrings={i18nStrings}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
      />
    </>
  );
};
